/**
 * BonificiList — Lista di tutti i bonifici disposti.
 *
 * Gestisce il callback OAuth dal provider bancario verificando
 * la presence del parametro payment_request_id nell'URL.
 * Permette di aggiornare lo stato dei singoli bonifici.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUrlState } from '@lib/useUrlState';
import { Plus, RefreshCw, Send } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
} from '@floraltech/ui';
import { useFormatters } from '@lib/formatters';
import { StatoBonificoBadge } from '../components/StatoBonificoBadge';

// --- Tipi -------------------------------------------------------------------

interface Bonifico {
  id: string;
  conto_id: string;
  iban_destinatario: string;
  nome_destinatario: string;
  importo: string;
  causale: string;
  stato: string;
  created_at: string;
  data_esecuzione: string | null;
  fornitore_id: string | null;
}

// --- Helpers ----------------------------------------------------------------

const authGet = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
  'Content-Type': 'application/json',
});

/** Tronca una stringa a maxLen caratteri aggiungendo '…'. */
function tronca(s: string, maxLen = 40): string {
  return s.length > maxLen ? `${s.slice(0, maxLen)}…` : s;
}

// Stati disponibili per il filtro
const STATI_FILTRO = [
  { value: '', label: 'Tutti gli stati' },
  { value: 'bozza', label: 'Bozza' },
  { value: 'in_attesa_sca', label: 'In attesa SCA' },
  { value: 'inviato', label: 'Inviato' },
  { value: 'eseguito', label: 'Eseguito' },
  { value: 'rifiutato', label: 'Rifiutato' },
  { value: 'annullato', label: 'Annullato' },
];

// --- Componente -------------------------------------------------------------

/**
 * Lista dei bonifici disposti con filtro stato e aggiornamento manuale.
 * Gestisce il redirect di ritorno dal provider bancario (callback OAuth).
 */
export function BonificiList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currency, date } = useFormatters();

  const [bonifici, setBonifici] = useState<Bonifico[]>([]);
  const [loading, setLoading] = useState(true);
  const [statoFiltro, setStatoFiltro] = useUrlState('stato', '');
  const [aggiornando, setAggiornando] = useState<string | null>(null);
  const [callbackMsg, setCallbackMsg] = useState<string | null>(null);

  /** Carica la lista bonifici con eventuale filtro stato. */
  const fetchBonifici = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statoFiltro) params.set('stato', statoFiltro);

    const res = await fetch(`/api/v1/banca/bonifici?${params}`, {
      headers: authGet(),
    });
    if (res.ok) {
      const data = await res.json();
      setBonifici(Array.isArray(data) ? data : (data.items ?? []));
    }
    setLoading(false);
  }, [statoFiltro]);

  // Gestione callback OAuth dal provider bancario
  useEffect(() => {
    const paymentRequestId = searchParams.get('payment_request_id');
    if (!paymentRequestId) return;

    const handleCallback = async () => {
      setCallbackMsg('Verifica stato autorizzazione…');
      try {
        const res = await fetch(
          `/api/v1/banca/bonifici/callback?payment_request_id=${encodeURIComponent(paymentRequestId)}`,
          { headers: authGet() },
        );
        if (res.ok) {
          setCallbackMsg('Autorizzazione ricevuta. Stato aggiornato.');
        } else {
          setCallbackMsg('Impossibile verificare lo stato del pagamento.');
        }
      } catch {
        setCallbackMsg('Errore di rete durante la verifica callback.');
      }
      // Rimuovi payment_request_id dall'URL dopo l'elaborazione
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('payment_request_id');
      setSearchParams(newParams, { replace: true });
      fetchBonifici();
    };

    handleCallback();
    // Eseguito solo al mount quando il parametro è presente
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carica bonifici al mount e quando cambia il filtro
  useEffect(() => {
    fetchBonifici();
  }, [fetchBonifici]);

  /**
   * Richiede aggiornamento stato di un singolo bonifico dal provider.
   *
   * @param id - UUID del bonifico
   */
  const aggiornaStato = useCallback(
    async (id: string) => {
      setAggiornando(id);
      try {
        const res = await fetch(`/api/v1/banca/bonifici/${id}/aggiorna-stato`, {
          method: 'POST',
          headers: authHeaders(),
        });
        if (res.ok) {
          await fetchBonifici();
        }
      } catch {
        // Errore silenzioso — lo stato rimane invariato
      } finally {
        setAggiornando(null);
      }
    },
    [fetchBonifici],
  );

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-outfit font-semibold text-foreground">Bonifici</h1>
        <Button onClick={() => navigate('/banca/bonifici/nuovo')}>
          <Plus className="h-4 w-4 mr-2" />
          Nuovo bonifico
        </Button>
      </div>

      {/* Messaggio callback */}
      {callbackMsg && (
        <div className="rounded bg-primary/10 border border-primary/20 px-4 py-2.5 text-sm text-primary flex items-center gap-2">
          <Send className="h-4 w-4 shrink-0" />
          {callbackMsg}
        </div>
      )}

      {/* Filtro stato */}
      <div className="flex gap-3 items-center">
        <select
          value={statoFiltro}
          onChange={(e) => setStatoFiltro(e.target.value)}
          className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm"
        >
          {STATI_FILTRO.map((s) => (
            <option key={s.value || '__all__'} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchBonifici}
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Aggiorna
        </Button>
      </div>

      {/* Tabella */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground">Caricamento…</div>
        ) : bonifici.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            Nessun bonifico disposto
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left px-4 py-2 font-medium text-muted-foreground">
                    Data
                  </th>
                  <th className="text-left px-4 py-2 font-medium text-muted-foreground">
                    Destinatario
                  </th>
                  <th className="text-left px-4 py-2 font-medium text-muted-foreground">
                    IBAN
                  </th>
                  <th className="text-right px-4 py-2 font-medium text-muted-foreground">
                    Importo
                  </th>
                  <th className="text-left px-4 py-2 font-medium text-muted-foreground">
                    Causale
                  </th>
                  <th className="text-left px-4 py-2 font-medium text-muted-foreground">
                    Stato
                  </th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {bonifici.map((b) => (
                  <tr
                    key={b.id}
                    className="border-b border-border hover:bg-muted/20 transition-colors duration-150"
                  >
                    <td className="px-4 py-2 whitespace-nowrap tabular-nums text-muted-foreground">
                      {date(b.created_at)}
                    </td>
                    <td className="px-4 py-2 font-medium">{b.nome_destinatario}</td>
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                      {b.iban_destinatario}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium">
                      {currency(b.importo)}
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">
                      {tronca(b.causale)}
                    </td>
                    <td className="px-4 py-2">
                      <StatoBonificoBadge stato={b.stato} />
                    </td>
                    <td className="px-4 py-2">
                      {/* Mostra il pulsante solo per stati non terminali */}
                      {!['eseguito', 'annullato'].includes(b.stato) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={aggiornando === b.id}
                          onClick={() => aggiornaStato(b.id)}
                          title="Aggiorna stato dal provider"
                        >
                          <RefreshCw
                            className={`h-3.5 w-3.5 ${aggiornando === b.id ? 'animate-spin' : ''}`}
                          />
                          <span className="ml-1 text-xs">Aggiorna stato</span>
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

export default BonificiList;
